import { useMemo, useState, type ChangeEvent } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { DashboardShell } from '@/components/layout/DashboardShell';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal';
import { TypeConfirmDialog } from '@/components/ui/TypeConfirmDialog';
import { adminService, type TeacherAccount } from '@/services/admin';
import type { ClassSummary } from '@/types';
import { resolvePersonPhoto } from '@/utils/avatars';
import { AvatarUploader } from '@/components/ui/AvatarUploader';
import { PhotoLightbox, PhotoThumb } from '@/components/ui/PhotoLightbox';
import { generateStrongPassword } from '@/utils/password';

type Period = 'daily' | 'weekly' | 'monthly' | 'yearly';
type StatusFilter = 'active' | 'inactive' | 'all';

const isTeacherInactive = (teacher: TeacherAccount) => teacher.status === 'Terminated';

const fieldClassName =
  'w-full rounded-lg border border-slate-300 px-3 py-2 text-base sm:text-sm min-h-[40px] shadow-sm focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/30';
const fieldMonoClassName =
  'w-full rounded-lg border border-slate-300 px-3 py-2 pr-10 text-base sm:text-sm min-h-[40px] font-mono shadow-sm focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/30';

const resolveErrorMessage = (error: unknown, fallback: string) => {
  if (error && typeof error === 'object' && 'response' in error) {
    const response = (error as any).response;
    return response?.data?.message ?? response?.data?.error ?? fallback;
  }

  if (error instanceof Error) {
    return error.message;
  }

  return fallback;
};

export default function AdminTeachersPage() {
  const navigate = useNavigate();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedTeacher, setSelectedTeacher] = useState<string | null>(null);
  const [editingTeacher, setEditingTeacher] = useState<TeacherAccount | null>(null);
  const [period, setPeriod] = useState<Period>('monthly');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('active');
  const [pendingDeactivate, setPendingDeactivate] = useState<TeacherAccount | null>(null);
  const [deactivateTyped, setDeactivateTyped] = useState('');
  const [previewSrc, setPreviewSrc] = useState<string | null>(null);
  const [showCreatePassword, setShowCreatePassword] = useState(false);
  const [showEditPassword, setShowEditPassword] = useState(false);
  const [showEditCurrentPassword, setShowEditCurrentPassword] = useState(false);
  const [copiedEditPassword, setCopiedEditPassword] = useState(false);
  const [visiblePasswords, setVisiblePasswords] = useState<Record<string, boolean>>({});
  const [copiedTeacherId, setCopiedTeacherId] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    classId: '',
    photo: '',
    phone: '',
    address: '',
    qualifications: '',
    cnic: '',
  });
  const [editFormData, setEditFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    classId: '',
    photo: '',
    phone: '',
    address: '',
    qualifications: '',
    cnic: '',
  });
  const queryClient = useQueryClient();

  const { data: teachers, isLoading: loadingTeachers } = useQuery<TeacherAccount[]>({
    queryKey: ['admin-teachers'],
    queryFn: adminService.listTeachers,
  });

  const teacherList = Array.isArray(teachers) ? teachers : [];
  const activeCount = teacherList.filter((teacher) => !isTeacherInactive(teacher)).length;
  const inactiveCount = teacherList.filter(isTeacherInactive).length;
  const visibleTeachers = useMemo(() => {
    return teacherList.filter((teacher) => {
      if (statusFilter === 'active') return !isTeacherInactive(teacher);
      if (statusFilter === 'inactive') return isTeacherInactive(teacher);
      return true;
    });
  }, [teacherList, statusFilter]);

  const { data: classes = [] } = useQuery<ClassSummary[]>({
    queryKey: ['admin-classes'],
    queryFn: adminService.listClasses,
  });

  const { data: teacherDetail } = useQuery({
    queryKey: ['teacher-detail', selectedTeacher, period],
    queryFn: () => selectedTeacher ? adminService.getTeacherDetail(selectedTeacher, { period }) : null,
    enabled: !!selectedTeacher,
  });

  const closeCreateModal = () => {
    setIsModalOpen(false);
    setFormData({ firstName: '', lastName: '', email: '', password: '', classId: '', photo: '', phone: '', address: '', qualifications: '', cnic: '' });
    setShowCreatePassword(false);
  };

  const openCreateModal = () => {
    setFormData({ firstName: '', lastName: '', email: '', password: '', classId: '', photo: '', phone: '', address: '', qualifications: '', cnic: '' });
    setIsModalOpen(true);
  };

  const closeEditModal = () => {
    setIsEditModalOpen(false);
    setEditingTeacher(null);
    setEditFormData({ firstName: '', lastName: '', email: '', password: '', classId: '', photo: '', phone: '', address: '', qualifications: '', cnic: '' });
    setShowEditPassword(false);
    setShowEditCurrentPassword(false);
    setCopiedEditPassword(false);
  };

  const openEditModal = (teacher: TeacherAccount) => {
    setEditingTeacher(teacher);
    setShowEditCurrentPassword(false);
    setCopiedEditPassword(false);
    setEditFormData({
      firstName: teacher.firstName,
      lastName: teacher.lastName,
      email: teacher.email,
      password: '',
      classId: teacher.assignedClasses[0]?.id ?? '',
      photo: teacher.photo || '',
      phone: teacher.phone || '',
      address: teacher.address || '',
      qualifications: teacher.qualifications || '',
      cnic: teacher.cnic || '',
    });
    setIsEditModalOpen(true);
  };

  const handleCreateUpload = async (file: File) => {
    const data = await adminService.uploadAvatar(file);
    setFormData(prev => ({ ...prev, photo: data.url }));
  };

  const handleEditUpload = async (file: File) => {
    const data = await adminService.uploadAvatar(file);
    setEditFormData(prev => ({ ...prev, photo: data.url }));
  };

  const createMutation = useMutation({
    mutationFn: adminService.createTeacher,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-teachers'] });
      queryClient.invalidateQueries({ queryKey: ['admin-overview'] });
      queryClient.invalidateQueries({ queryKey: ['admin-classes'] });
      closeCreateModal();
    },
    onError: (error) => {
      alert(resolveErrorMessage(error, 'Unable to create teacher.'));
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: { firstName?: string; lastName?: string; email?: string; password?: string; classId?: string | null; photo?: string; phone?: string; address?: string; qualifications?: string; cnic?: string } }) =>
      adminService.updateTeacher(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-teachers'] });
      queryClient.invalidateQueries({ queryKey: ['admin-overview'] });
      queryClient.invalidateQueries({ queryKey: ['admin-classes'] });
      queryClient.invalidateQueries({ queryKey: ['teacher-detail'] });
      closeEditModal();
    },
    onError: (error) => {
      alert(resolveErrorMessage(error, 'Unable to update teacher.'));
    },
  });

  const invalidateTeachers = () => {
    queryClient.invalidateQueries({ queryKey: ['admin-teachers'] });
    queryClient.invalidateQueries({ queryKey: ['admin-overview'] });
    queryClient.invalidateQueries({ queryKey: ['admin-classes'] });
  };

  const deactivateMutation = useMutation({
    mutationFn: adminService.deactivateTeacher,
    onSuccess: () => {
      setPendingDeactivate(null);
      setDeactivateTyped('');
      invalidateTeachers();
    },
    onError: (error) => alert(resolveErrorMessage(error, 'Unable to deactivate teacher.')),
  });

  const restoreMutation = useMutation({
    mutationFn: adminService.restoreTeacher,
    onSuccess: invalidateTeachers,
    onError: (error) => alert(resolveErrorMessage(error, 'Unable to restore teacher.')),
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!(formData.firstName && formData.lastName && formData.email && formData.password)) {
      return;
    }
    if (formData.classId) {
      const klass = classes.find((item) => item.id === formData.classId);
      if (klass?.teacherId) {
        const ok = window.confirm(
          'This class already has a homeroom teacher. Assigning it here takes the class from them. Continue?',
        );
        if (!ok) return;
      }
    }
    createMutation.mutate({
      firstName: formData.firstName,
      lastName: formData.lastName,
      email: formData.email,
      password: formData.password,
      classId: formData.classId ? formData.classId : null,
      photo: formData.photo,
      phone: formData.phone,
      qualifications: formData.qualifications,
    });
  };

  const handleEditSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingTeacher) {
      return;
    }

    if (editFormData.firstName && editFormData.lastName && editFormData.email) {
      if (editFormData.cnic.trim()) {
        const digits = editFormData.cnic.replace(/\D+/g, '');
        if (digits.length !== 13) {
          alert('CNIC must be 13 digits (dashes allowed), or left empty.');
          return;
        }
      }
      if (editFormData.classId) {
        const klass = classes.find((item) => item.id === editFormData.classId);
        const takingOther = Boolean(klass?.teacherId && klass.teacherId !== editingTeacher.id);
        if (takingOther) {
          const ok = window.confirm(
            'This class already has a homeroom teacher. Assigning it here takes the class from them. Continue?',
          );
          if (!ok) return;
        }
      }
      const payload: { firstName: string; lastName: string; email: string; password?: string; classId: string | null; photo?: string; phone?: string; address?: string; qualifications?: string; cnic?: string } = {
        firstName: editFormData.firstName,
        lastName: editFormData.lastName,
        email: editFormData.email,
        classId: editFormData.classId ? editFormData.classId : null,
        photo: editFormData.photo,
        phone: editFormData.phone,
        address: editFormData.address,
        qualifications: editFormData.qualifications,
        cnic: editFormData.cnic,
      };

      if (editFormData.password.trim()) {
        payload.password = editFormData.password;
      }

      updateMutation.mutate({ id: editingTeacher.id, data: payload });
    }
  };

  const getPercentage = (value: number, total: number) => {
    return total > 0 ? ((value / total) * 100).toFixed(1) : '0.0';
  };

  return (
    <DashboardShell title="Teacher Management" role="admin">
      <Card
        title={loadingTeachers ? 'Teachers' : `Teachers (${activeCount} active)`}
        actions={<Button variant="primary" onClick={openCreateModal}>Add Teacher</Button>}
      >
        <div className="mb-4 max-w-xs">
          <select
            className={fieldClassName}
            aria-label="Teacher status"
            value={statusFilter}
            onChange={(event) => setStatusFilter(event.target.value as StatusFilter)}
          >
            <option value="active">Active ({loadingTeachers ? '…' : activeCount})</option>
            <option value="inactive">Inactive ({loadingTeachers ? '…' : inactiveCount})</option>
            <option value="all">All ({loadingTeachers ? '…' : teacherList.length})</option>
          </select>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-slate-200">
            <thead className="bg-slate-50">
              <tr>
                <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Name
                </th>
                <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Email
                </th>
                <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Password
                </th>
                <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Classes
                </th>
                <th className="px-3 py-2 text-center text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200">
              {(visibleTeachers).map((teacher: TeacherAccount) => (
                <tr key={teacher.id} className="bg-white hover:bg-slate-50 transition-colors">
                  <td className="px-3 py-3 text-sm font-medium text-slate-900">
                    <div className="flex items-center gap-3">
                      <PhotoThumb
                        src={resolvePersonPhoto(teacher, 'teacher')}
                        onClick={() => setPreviewSrc(resolvePersonPhoto(teacher, 'teacher'))}
                      />
                      <span>
                        {teacher.firstName} {teacher.lastName}
                        {isTeacherInactive(teacher) ? (
                          <span className="ml-2 text-xs font-normal text-slate-400">Inactive</span>
                        ) : null}
                      </span>
                    </div>
                  </td>
                  <td className="px-3 py-3 text-sm text-slate-600">{teacher.email}</td>
                  <td className="px-3 py-3 text-sm text-slate-600">
                    <div className="flex items-center gap-1.5 font-mono text-xs">
                      <span className="bg-slate-100 rounded px-2 py-0.5 max-w-[150px] truncate select-all">
                        {visiblePasswords[teacher.id]
                          ? (teacher.loginPassword || '—')
                          : (teacher.loginPassword ? '••••••••••••' : '—')}
                      </span>
                      {teacher.loginPassword && (
                        <>
                          <button
                            type="button"
                            onClick={() => setVisiblePasswords(prev => ({ ...prev, [teacher.id]: !prev[teacher.id] }))}
                            className="rounded p-1 text-slate-400 hover:text-slate-700 focus:outline-none"
                            title={visiblePasswords[teacher.id] ? 'Hide password' : 'Show password'}
                            aria-label={visiblePasswords[teacher.id] ? 'Hide password' : 'Show password'}
                          >
                            {visiblePasswords[teacher.id] ? (
                              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M3.53 3.53l16.94 16.94" />
                                <path strokeLinecap="round" strokeLinejoin="round" d="M9.88 9.88a3 3 0 104.24 4.24" />
                                <path strokeLinecap="round" strokeLinejoin="round" d="M6.23 6.23C3.8 7.85 2.25 10.09 2.25 12c0 0 3.75 7.5 9.75 7.5 1.12 0 2.18-.2 3.17-.56" />
                                <path strokeLinecap="round" strokeLinejoin="round" d="M17.77 17.77C20.2 16.15 21.75 13.91 21.75 12c0 0-3.75-7.5-9.75-7.5-1.12 0-2.18.2-3.17.56" />
                              </svg>
                            ) : (
                              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 12c2.25-3.75 5.25-6 9.75-6s7.5 2.25 9.75 6c-2.25 3.75-5.25 6-9.75 6s-7.5-2.25-9.75-6z" />
                                <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                              </svg>
                            )}
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              if (teacher.loginPassword) {
                                navigator.clipboard.writeText(teacher.loginPassword);
                                setCopiedTeacherId(teacher.id);
                                setTimeout(() => setCopiedTeacherId(null), 2000);
                              }
                            }}
                            className="rounded p-1 text-slate-400 hover:text-slate-700 focus:outline-none"
                            title="Copy password"
                            aria-label="Copy password"
                          >
                            {copiedTeacherId === teacher.id ? (
                              <span className="text-emerald-600 text-xs font-sans font-semibold">✓</span>
                            ) : (
                              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 17.25v3.375c0 .621-.504 1.125-1.125 1.125h-9.75a1.125 1.125 0 01-1.125-1.125V7.875c0-.621.504-1.125 1.125-1.125H6.75a9.06 9.06 0 011.5.124m7.5 10.376h3.375c.621 0 1.125-.504 1.125-1.125V11.25c0-4.46-3.243-8.161-7.5-8.876a9.06 9.06 0 00-1.5-.124H9.375c-.621 0-1.125.504-1.125 1.125v3.5m7.5 10.375H9.375a1.125 1.125 0 01-1.125-1.125v-9.25m12 6.625v-1.875a3.375 3.375 0 00-3.375-3.375h-1.5a1.125 1.125 0 01-1.125-1.125v-1.5a3.375 3.375 0 00-3.375-3.375H9.75" />
                              </svg>
                            )}
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                  <td className="px-3 py-3 text-sm text-slate-600">
                    {teacher.assignedClasses.map((klass) => klass.name).join(', ') || '—'}
                  </td>
                  <td className="px-3 py-3 text-sm text-center">
                    <div className="flex justify-center gap-2">
                      <Button
                        variant="secondary"
                        onClick={() => openEditModal(teacher)}
                        className="text-xs"
                      >
                        Edit
                      </Button>
                      <Button
                        variant="secondary"
                        onClick={() => setSelectedTeacher(teacher.id)}
                        className="text-xs"
                      >
                        View Summary
                      </Button>
                      {isTeacherInactive(teacher) ? (
                        <Button
                          variant="secondary"
                          onClick={() => restoreMutation.mutate(teacher.id)}
                          className="text-xs"
                          disabled={restoreMutation.isPending}
                        >
                          Restore
                        </Button>
                      ) : (
                        <Button
                          variant="secondary"
                          onClick={() => {
                            setDeactivateTyped('');
                            setPendingDeactivate(teacher);
                          }}
                          className="text-xs text-rose-700"
                        >
                          Deactivate
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
              {!visibleTeachers.length && (
                <tr>
                  <td colSpan={5} className="px-3 py-6 text-center text-sm text-slate-500">
                    {loadingTeachers
                      ? 'Loading teachers…'
                      : 'No teachers match this filter. Switch to All or add a teacher.'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Add Teacher Modal */}
      <Modal
        isOpen={isModalOpen}
        onClose={closeCreateModal}
        title="Add New Teacher"
        footer={
          <>
            <Button variant="secondary" onClick={closeCreateModal}>
              Cancel
            </Button>
            <Button 
              variant="primary" 
              type="button"
              onClick={handleSubmit}
              disabled={createMutation.isPending}
            >
              {createMutation.isPending ? 'Creating...' : 'Create Teacher'}
            </Button>
          </>
        }
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Photo
            </label>
            <div className="flex justify-center mb-4">
              <AvatarUploader 
                currentPhotoUrl={formData.photo} 
                onUpload={handleCreateUpload} 
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                First Name
              </label>
            <input
              type="text"
              required
              className={fieldClassName}
              value={formData.firstName}
              onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Last Name
            </label>
            <input
              type="text"
              required
              className={fieldClassName}
              value={formData.lastName}
              onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
            />
          </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Email
            </label>
            <input
              type="email"
              required
              className={fieldClassName}
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            />
          </div>
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="block text-sm font-medium text-slate-700">
                Password
              </label>
              <button
                type="button"
                onClick={() => {
                  const generated = generateStrongPassword(16);
                  setFormData((prev) => ({ ...prev, password: generated }));
                  setShowCreatePassword(true);
                }}
                className="text-xs font-semibold text-brand hover:text-brand-dark focus:outline-none"
              >
                ✨ Generate Password
              </button>
            </div>
            <div className="relative">
              <input
                type={showCreatePassword ? 'text' : 'password'}
                required
                minLength={12}
                placeholder="Minimum 12 characters (or click Generate)"
                className={fieldMonoClassName}
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              />
              <button
                type="button"
                onClick={() => setShowCreatePassword((prev) => !prev)}
                className="absolute inset-y-0 right-2 flex items-center rounded px-1 text-slate-500 transition hover:text-slate-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand"
                aria-label={showCreatePassword ? 'Hide password' : 'Show password'}
              >
                {showCreatePassword ? (
                  <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M3.53 3.53l16.94 16.94" />
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9.88 9.88a3 3 0 104.24 4.24" />
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6.23 6.23C3.8 7.85 2.25 10.09 2.25 12c0 0 3.75 7.5 9.75 7.5 1.12 0 2.18-.2 3.17-.56" />
                    <path strokeLinecap="round" strokeLinejoin="round" d="M17.77 17.77C20.2 16.15 21.75 13.91 21.75 12c0 0-3.75-7.5-9.75-7.5-1.12 0-2.18.2-3.17.56" />
                  </svg>
                ) : (
                  <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 12c2.25-3.75 5.25-6 9.75-6s7.5 2.25 9.75 6c-2.25 3.75-5.25 6-9.75 6s-7.5-2.25-9.75-6z" />
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                )}
              </button>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Assign Class (optional)
            </label>
            <select
              className={fieldClassName}
              value={formData.classId}
              onChange={(e) => setFormData({ ...formData, classId: e.target.value })}
            >
              <option value="">Unassigned</option>
              {classes.map((klass) => {
                const teacherLabel = klass.teacherName ? ` – Assigned to ${klass.teacherName}` : ' – Unassigned';
                const gradeLabel = klass.gradeLevel && klass.gradeLevel !== klass.name ? ` (${klass.gradeLevel})` : '';
                return (
                  <option key={klass.id} value={klass.id}>
                    {klass.name}
                    {gradeLabel}
                    {teacherLabel}
                  </option>
                );
              })}
            </select>
            <p className="mt-1 text-xs text-slate-500">Selecting a class here will assign it to the new teacher.</p>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Phone Number
              </label>
              <input
                type="tel"
                className={fieldClassName}
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Qualifications
              </label>
              <input
                type="text"
                placeholder="e.g. M.Sc. Mathematics"
                className={fieldClassName}
                value={formData.qualifications}
                onChange={(e) => setFormData({ ...formData, qualifications: e.target.value })}
              />
            </div>
          </div>
        </form>
      </Modal>

      {/* Edit Teacher Modal */}
      <Modal
        isOpen={isEditModalOpen}
        onClose={closeEditModal}
        title={editingTeacher ? `Edit Teacher • ${editingTeacher.firstName} ${editingTeacher.lastName}` : 'Edit Teacher'}
        footer={
          <>
            <Button variant="secondary" onClick={closeEditModal}>
              Cancel
            </Button>
            <Button
              variant="primary"
              type="button"
              onClick={handleEditSubmit}
              disabled={updateMutation.isPending}
            >
              {updateMutation.isPending ? 'Saving...' : 'Save Changes'}
            </Button>
          </>
        }
      >
        <form onSubmit={handleEditSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Photo
            </label>
            <div className="flex justify-center mb-4">
              <AvatarUploader 
                currentPhotoUrl={editFormData.photo} 
                onUpload={handleEditUpload} 
              />
            </div>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">First Name</label>
              <input
                type="text"
                required
                className={fieldClassName}
                value={editFormData.firstName}
                onChange={(e) => setEditFormData({ ...editFormData, firstName: e.target.value })}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Last Name</label>
              <input
                type="text"
                required
                className={fieldClassName}
                value={editFormData.lastName}
                onChange={(e) => setEditFormData({ ...editFormData, lastName: e.target.value })}
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Email</label>
            <input
              type="email"
              required
              className={fieldClassName}
              value={editFormData.email}
              onChange={(e) => setEditFormData({ ...editFormData, email: e.target.value })}
            />
          </div>

          {editingTeacher?.loginPassword && (
            <div className="rounded-lg bg-slate-50 border border-slate-200 p-3">
              <div className="flex items-center justify-between">
                <span className="text-xs font-medium text-slate-600 uppercase tracking-wide">Current Password</span>
                <div className="flex items-center gap-2 font-mono text-xs">
                  <span className="font-semibold text-slate-900 bg-white border border-slate-200 px-2 py-0.5 rounded select-all">
                    {showEditCurrentPassword ? editingTeacher.loginPassword : '••••••••••••'}
                  </span>
                  <button
                    type="button"
                    onClick={() => setShowEditCurrentPassword((p) => !p)}
                    className="text-slate-500 hover:text-slate-700 underline text-xs font-sans"
                    title={showEditCurrentPassword ? 'Hide' : 'Show'}
                  >
                    {showEditCurrentPassword ? 'Hide' : 'Show'}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      if (editingTeacher.loginPassword) {
                        navigator.clipboard.writeText(editingTeacher.loginPassword);
                        setCopiedEditPassword(true);
                        setTimeout(() => setCopiedEditPassword(false), 2000);
                      }
                    }}
                    className="text-slate-500 hover:text-slate-700 font-sans text-xs"
                  >
                    {copiedEditPassword ? '✓ Copied' : 'Copy'}
                  </button>
                </div>
              </div>
            </div>
          )}

          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="block text-sm font-medium text-slate-700">New Password (optional)</label>
              <button
                type="button"
                onClick={() => {
                  const generated = generateStrongPassword(16);
                  setEditFormData((prev) => ({ ...prev, password: generated }));
                  setShowEditPassword(true);
                }}
                className="text-xs font-semibold text-brand hover:text-brand-dark focus:outline-none"
              >
                ✨ Generate Password
              </button>
            </div>
            <div className="relative">
              <input
                type={showEditPassword ? 'text' : 'password'}
                minLength={12}
                placeholder="Leave blank to keep current password"
                className={fieldMonoClassName}
                value={editFormData.password}
                onChange={(e) => setEditFormData({ ...editFormData, password: e.target.value })}
              />
              <button
                type="button"
                onClick={() => setShowEditPassword((prev) => !prev)}
                className="absolute inset-y-0 right-2 flex items-center rounded px-1 text-slate-500 transition hover:text-slate-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand"
                aria-label={showEditPassword ? 'Hide password' : 'Show password'}
              >
                {showEditPassword ? (
                  <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M3.53 3.53l16.94 16.94" />
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9.88 9.88a3 3 0 104.24 4.24" />
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6.23 6.23C3.8 7.85 2.25 10.09 2.25 12c0 0 3.75 7.5 9.75 7.5 1.12 0 2.18-.2 3.17-.56" />
                    <path strokeLinecap="round" strokeLinejoin="round" d="M17.77 17.77C20.2 16.15 21.75 13.91 21.75 12c0 0-3.75-7.5-9.75-7.5-1.12 0-2.18.2-3.17.56" />
                  </svg>
                ) : (
                  <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 12c2.25-3.75 5.25-6 9.75-6s7.5 2.25 9.75 6c-2.25 3.75-5.25 6-9.75 6s-7.5-2.25-9.75-6z" />
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                )}
              </button>
            </div>
            <p className="mt-1 text-xs text-slate-500">Leave blank to keep current password, or enter a new password (min 12 chars) to reset.</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Assign Class</label>
            <select
              className={fieldClassName}
              value={editFormData.classId}
              onChange={(e) => setEditFormData({ ...editFormData, classId: e.target.value })}
            >
              <option value="">Unassigned</option>
              {classes.map((klass) => {
                const isAssignedToCurrent = editingTeacher?.assignedClasses?.some((assigned) => assigned.id === klass.id) ?? false;
                let assignmentLabel = ' – Unassigned';
                if (klass.teacherName) {
                  assignmentLabel = isAssignedToCurrent ? ' – Assigned to this teacher' : ` – Assigned to ${klass.teacherName}`;
                }
                const gradeLabel = klass.gradeLevel && klass.gradeLevel !== klass.name ? ` (${klass.gradeLevel})` : '';
                return (
                  <option key={klass.id} value={klass.id}>
                    {klass.name}
                    {gradeLabel}
                    {assignmentLabel}
                  </option>
                );
              })}
            </select>
            <p className="mt-1 text-xs text-slate-500">Changing this selection will immediately reassign the class.</p>
            {editingTeacher && (
              <p className="mt-1 text-xs text-slate-500">
                Current classes: {editingTeacher.assignedClasses.length ? editingTeacher.assignedClasses.map((klass) => klass.name).join(', ') : 'None'}
              </p>
            )}
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Phone Number
              </label>
              <input
                type="tel"
                className={fieldClassName}
                value={editFormData.phone}
                onChange={(e) => setEditFormData({ ...editFormData, phone: e.target.value })}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Qualifications
              </label>
              <input
                type="text"
                placeholder="e.g. M.Sc. Mathematics"
                className={fieldClassName}
                value={editFormData.qualifications}
                onChange={(e) => setEditFormData({ ...editFormData, qualifications: e.target.value })}
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              CNIC
            </label>
            <input
              type="text"
              placeholder="e.g. 12345-1234567-1"
              className={fieldClassName}
              value={editFormData.cnic}
              onChange={(e) => setEditFormData({ ...editFormData, cnic: e.target.value })}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Address
            </label>
            <textarea
              rows={2}
              className={fieldClassName}
              value={editFormData.address}
              onChange={(e) => setEditFormData({ ...editFormData, address: e.target.value })}
            />
          </div>
        </form>
      </Modal>

      <Modal
        isOpen={!!selectedTeacher}
        onClose={() => setSelectedTeacher(null)}
        title={teacherDetail ? `${teacherDetail.teacher.firstName} ${teacherDetail.teacher.lastName} - Summary` : 'Teacher Summary'}
        footer={teacherDetail ? (
          <Button variant="secondary" onClick={() => navigate(`/admin/reports/teachers/${teacherDetail.teacher.id}`)}>
            View report
          </Button>
        ) : null}
      >
        {teacherDetail && (
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Time Period
              </label>
              <div className="grid grid-cols-4 gap-2">
                {(['daily', 'weekly', 'monthly', 'yearly'] as Period[]).map((p) => {
                  const periodLabels = {
                    daily: 'Today',
                    weekly: 'Last Week',
                    monthly: 'Last Month',
                    yearly: 'Last Year'
                  };
                  const periodDescriptions = {
                    daily: 'Present day',
                    weekly: 'Last 6 weekdays',
                    monthly: 'Last 30 weekdays',
                    yearly: 'Last 365 weekdays'
                  };
                  return (
                    <button
                      key={p}
                      onClick={() => setPeriod(p)}
                      className={`px-3 py-2 text-sm font-medium rounded-lg border transition-colors ${
                        period === p
                          ? 'bg-brand text-white border-brand'
                          : 'bg-white text-slate-700 border-slate-300 hover:bg-slate-50'
                      }`}
                      title={periodDescriptions[p]}
                    >
                      {periodLabels[p]}
                    </button>
                  );
                })}
              </div>
              <p className="text-xs text-slate-500 mt-2">
                {period === 'daily' && '📅 Showing present day only'}
                {period === 'weekly' && '📅 Showing last 6 weekdays (Mon-Fri)'}
                {period === 'monthly' && '📅 Showing last 30 weekdays (Mon-Fri)'}
                {period === 'yearly' && '📅 Showing last 365 weekdays (Mon-Fri)'}
              </p>
            </div>

            <div className="bg-slate-50 border border-slate-200 rounded-lg p-5">
              <div className="flex items-start gap-4 mb-4 pb-4 border-b border-slate-200">
                <img
                  src={resolvePersonPhoto(teacherDetail.teacher, 'teacher')}
                  alt=""
                  className="w-16 h-16 rounded-full object-cover border-2 border-white shadow-sm"
                />
                <div className="flex-1 min-w-0">
                  <h3 className="text-lg font-bold text-slate-900">
                    {teacherDetail.teacher.firstName} {teacherDetail.teacher.lastName}
                  </h3>
                  <p className="text-sm text-slate-500">{teacherDetail.teacher.email}</p>
                </div>
                <Button variant="secondary" onClick={() => navigate(`/admin/reports/teachers/${teacherDetail.teacher.id}`)}>
                  View report
                </Button>
              </div>
              <div className="grid grid-cols-2 gap-y-4 gap-x-6">
                <div>
                  <p className="text-xs font-medium text-slate-500 uppercase">Phone Number</p>
                  <p className="text-sm text-slate-900 font-medium">{teacherDetail.teacher.phone || '—'}</p>
                </div>
                <div>
                  <p className="text-xs font-medium text-slate-500 uppercase">Qualifications</p>
                  <p className="text-sm text-slate-900 font-medium">{teacherDetail.teacher.qualifications || '—'}</p>
                </div>
                <div className="col-span-2">
                  <p className="text-xs font-medium text-slate-500 uppercase">Address</p>
                  <p className="text-sm text-slate-900 font-medium">{teacherDetail.teacher.address || '—'}</p>
                </div>
              </div>
            </div>

            <div>
              <h4 className="text-sm font-semibold text-slate-900 mb-3">Attendance Summary</h4>
              <div className="grid grid-cols-5 gap-3">
                <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
                  <p className="text-xs font-medium text-slate-500 uppercase mb-1">Total</p>
                  <p className="text-2xl font-bold text-slate-900">{teacherDetail.attendance.total}</p>
                </div>
                <div className="bg-emerald-50 border-2 border-emerald-200 rounded-lg p-3">
                  <p className="text-xs font-medium text-emerald-700 uppercase mb-1">Present</p>
                  <p className="text-2xl font-bold text-emerald-700">{teacherDetail.attendance.present}</p>
                  <p className="text-xs text-emerald-600 mt-1">
                    {getPercentage(teacherDetail.attendance.present, teacherDetail.attendance.total)}%
                  </p>
                </div>
                <div className="bg-rose-50 border-2 border-rose-200 rounded-lg p-3">
                  <p className="text-xs font-medium text-rose-700 uppercase mb-1">Absent</p>
                  <p className="text-2xl font-bold text-rose-700">{teacherDetail.attendance.absent}</p>
                  <p className="text-xs text-rose-600 mt-1">
                    {getPercentage(teacherDetail.attendance.absent, teacherDetail.attendance.total)}%
                  </p>
                </div>
                <div className="bg-amber-50 border-2 border-amber-200 rounded-lg p-3">
                  <p className="text-xs font-medium text-amber-700 uppercase mb-1">Late</p>
                  <p className="text-2xl font-bold text-amber-700">{teacherDetail.attendance.late}</p>
                  <p className="text-xs text-amber-600 mt-1">
                    {getPercentage(teacherDetail.attendance.late, teacherDetail.attendance.total)}%
                  </p>
                </div>
                <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-3">
                  <p className="text-xs font-medium text-blue-700 uppercase mb-1">Leave</p>
                  <p className="text-2xl font-bold text-blue-700">{teacherDetail.attendance.leave}</p>
                  <p className="text-xs text-blue-600 mt-1">
                    {getPercentage(teacherDetail.attendance.leave, teacherDetail.attendance.total)}%
                  </p>
                </div>
              </div>
            </div>

            <div>
              <h4 className="text-sm font-semibold text-slate-900 mb-3">Assigned Classes</h4>
              {teacherDetail.classes.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-slate-200">
                    <thead className="bg-slate-50">
                      <tr>
                        <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                          Class Name
                        </th>
                        <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                          Grade
                        </th>
                        <th className="px-3 py-2 text-right text-xs font-semibold uppercase tracking-wide text-slate-500">
                          Students
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-200 bg-white">
                      {teacherDetail.classes.map((klass) => (
                        <tr key={klass.id}>
                          <td className="px-3 py-2 text-sm font-medium text-slate-900">
                            {klass.name}
                          </td>
                          <td className="px-3 py-2 text-sm text-slate-600">
                            {klass.gradeLevel}
                          </td>
                          <td className="px-3 py-2 text-sm text-slate-600 text-right">
                            {klass.studentCount}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="text-sm text-slate-500 italic">No classes assigned</p>
              )}
            </div>
          </div>
        )}
      </Modal>

      <TypeConfirmDialog
        isOpen={Boolean(pendingDeactivate)}
        title="Deactivate teacher"
        description="This marks the teacher inactive. The account and attendance history stay. Homeroom is cleared. They leave the active staff list."
        confirmWord="DEACTIVATE"
        confirmLabel="Deactivate"
        pending={deactivateMutation.isPending}
        pendingLabel="Saving…"
        confirmValue={deactivateTyped}
        onConfirmValueChange={setDeactivateTyped}
        onCancel={() => {
          setPendingDeactivate(null);
          setDeactivateTyped('');
        }}
        onConfirm={() => {
          if (!pendingDeactivate || deactivateTyped !== 'DEACTIVATE') return;
          deactivateMutation.mutate(pendingDeactivate.id);
        }}
      />

      <PhotoLightbox src={previewSrc} onClose={() => setPreviewSrc(null)} />
    </DashboardShell>
  );
}
